import React, { useState, useEffect, useMemo } from 'react';
import SummaryApi from '../common';
import { MdEdit, MdClose, MdRefresh, MdAdd } from "react-icons/md";

const AllProductsPackage = () => {
  const [packages, setPackages] = useState([]);
  const [activeProducts, setActiveProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    productId: "",
    size: "",
    weight: ""
  });

  const initialFormState = {
    id: "",
    product_id: "",
    pack_size: "",
    weight: "",
    price: 0,
    is_active: 1,
  };
  const [formData, setFormData] = useState(initialFormState);

  // --- AUTO-CALCULATE PRICE LOGIC ---
  useEffect(() => {
    if (formData.product_id && formData.pack_size) {
      // 1. Find the selected product to get its selling price
      const selectedProduct = activeProducts.find(
        (p) => String(p.product_id) === String(formData.product_id)
      );

      // 2. Parse the multiplier from pack_size (e.g., "20" or "20 units")
      const multiplier = parseFloat(formData.pack_size) || 0;
      
      // 3. Get selling price (ensure it exists)
      const basePrice = selectedProduct?.selling_price || 0;

      // 4. Calculate and update formData
      const calculatedPrice = (basePrice * multiplier).toFixed(2);
      
      setFormData(prev => ({
        ...prev,
        price: calculatedPrice
      }));
    }
  }, [formData.product_id, formData.pack_size, activeProducts]);

  const fetchData = async () => {
    try {
      const resPkg = await fetch(SummaryApi.product_unit_admin_retrieve_all.url);
      const dataPkg = await resPkg.json();
      setPackages(dataPkg?.data || dataPkg);

      const resProd = await fetch(SummaryApi.getActiveProduct.url);
      const dataProd = await resProd.json();
      setActiveProducts(dataProd?.data || dataProd);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const getProductDetails = (productId) => {
    return activeProducts.find((p) => String(p.product_id) === String(productId));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({ productId: "", size: "", weight: "" });
  };

  const filterOptions = useMemo(() => {
    const sizes = [...new Set(packages.map(p => p.pack_size))].filter(Boolean).sort();
    const weights = [...new Set(packages.map(p => p.weight))].filter(Boolean).sort((a, b) => a - b);
    return { sizes, weights };
  }, [packages]);

  const filteredPackages = packages.filter(pkg => {
    const matchTitle = filters.productId ? String(pkg.product_id) === filters.productId : true;
    const matchSize = filters.size ? pkg.pack_size === filters.size : true;
    const matchWeight = filters.weight ? String(pkg.weight) === String(filters.weight) : true;
    return matchTitle && matchSize && matchWeight;
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
     [name]: type === "checkbox" ? (checked ? 1 : 0) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const apiConfig = isEditing ? SummaryApi.product_unit_update : SummaryApi.product_unit_create;

    try {
      const response = await fetch(apiConfig.url, {
        method: apiConfig.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          product_id: Number(formData.product_id),
          weight: parseFloat(formData.weight),
          price: parseFloat(formData.price) 
        }),
      });
      if (response.ok) {
        setIsModalOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-[#fff5f6] min-h-screen">
      {/* HEADER */}
      <div className="flex flex-col mb-8 gap-6">
        <div className="flex justify-between items-end">
          <div>
            <h2 className="text-4xl font-bold text-[#d8315b]">Product Packages</h2>
            <p className="text-gray-500 mt-1">Manage unit sizes and auto-calculated pricing.</p>
          </div>
          <div className="flex gap-3">
             <button onClick={resetFilters} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-gray-500 bg-white border border-gray-200 rounded-xl hover:text-[#d8315b] transition shadow-sm">
               <MdRefresh size={20}/> Reset
             </button>
             <button onClick={openCreateModal} className="flex items-center gap-2 px-6 py-2 text-sm font-bold text-white bg-[#d8315b] rounded-xl hover:bg-[#b2284b] transition shadow-lg shadow-pink-100">
               <MdAdd size={22}/> Create Package
             </button>
          </div>
        </div>
        
        {/* FILTER BAR (unchanged) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-2xl shadow-sm border border-pink-100">
           {/* ... Filter Selects ... */}
           <div className="relative">
            <label className="text-[10px] font-bold text-gray-400 uppercase ml-2">Product</label>
            <select name="productId" value={filters.productId} onChange={handleFilterChange} className="w-full bg-gray-50 border-none rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-pink-300 outline-none appearance-none">
              <option value="">All Products</option>
              {activeProducts.map(prod => <option key={prod.product_id} value={prod.product_id}>{prod.product_title}</option>)}
            </select>
          </div>
          <div className="relative">
            <label className="text-[10px] font-bold text-gray-400 uppercase ml-2">Size</label>
            <select name="size" value={filters.size} onChange={handleFilterChange} className="w-full bg-gray-50 border-none rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-pink-300 outline-none appearance-none">
              <option value="">All Sizes</option>
              {filterOptions.sizes.map(size => <option key={size} value={size}>{size}</option>)}
            </select>
          </div>
          <div className="relative">
            <label className="text-[10px] font-bold text-gray-400 uppercase ml-2">Weight</label>
            <select name="weight" value={filters.weight} onChange={handleFilterChange} className="w-full bg-gray-50 border-none rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-pink-300 outline-none appearance-none">
              <option value="">All Weights</option>
              {filterOptions.weights.map(w => <option key={w} value={w}>{w} g</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* CARD GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {filteredPackages.map((pkg) => (
          <div key={pkg.id || pkg._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 transition hover:shadow-md">
            <h3 className="font-bold text-gray-800 line-clamp-1">{getProductDetails(pkg.product_id)?.product_title || "Unknown"}</h3>
            <div className="text-xs text-gray-500 mt-2">
              <p>📦 Size: {pkg.pack_size}</p>
              <p>⚖️ Weight: {pkg.weight}g</p>
            </div>
            <p className="text-[#d8315b] text-2xl font-bold my-3">₹{pkg.price}</p>
            <button onClick={() => { setFormData(pkg); setIsEditing(true); setIsModalOpen(true); }} className="w-full bg-[#f34a73] text-white py-2 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold">
              <MdEdit /> Edit
            </button>
          </div>
        ))}
      </div>

      {/* MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex justify-between items-center px-8 py-5 border-b">
              <h3 className="text-2xl font-bold">{isEditing ? "Edit Package" : "Create Package"}</h3>
              <button onClick={() => setIsModalOpen(false)}><MdClose size={28} /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Product *</label>
                <select name="product_id" value={formData.product_id} onChange={handleChange} required className="w-full bg-gray-50 border rounded-xl p-3 outline-none focus:ring-2 focus:ring-pink-300">
                  <option value="">Choose a product...</option>
                  {activeProducts.map(prod => (
                    <option key={prod.product_id} value={prod.product_id}>
                      {prod.product_title} (Price: ₹{prod.selling_price})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Pack Size (Multiplier) *</label>
                  <input type="number" name="pack_size" value={formData.pack_size} onChange={handleChange} required placeholder="e.g. 20" className="w-full bg-gray-50 border rounded-xl p-3 outline-none focus:ring-2 focus:ring-pink-300" />
                  <p className="text-[10px] text-gray-400 mt-1">Price will be calculated as: Selling Price × Size</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Weight (g) *</label>
                  <input type="number" name="weight" value={formData.weight} onChange={handleChange} required placeholder="e.g. 500" className="w-full bg-gray-50 border rounded-xl p-3 outline-none focus:ring-2 focus:ring-pink-300" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Calculated Price (₹)</label>
                  <input 
                    type="number" 
                    name="price" 
                    value={formData.price} 
                    readOnly 
                    className="w-full bg-gray-100 border border-gray-200 rounded-xl p-3 font-bold text-[#d8315b] cursor-not-allowed"
                  />
                </div>
                <div className="flex items-center gap-2 px-1">
  <input 
    type="checkbox" 
    name="is_active" 
    id="is_active"
    checked={formData.is_active} 
    onChange={handleChange}
    className="w-5 h-5 accent-[#d8315b] cursor-pointer"
  />
  <label htmlFor="is_active" className="text-sm font-semibold text-gray-700 cursor-pointer">
    Active (Visible to customers)
  </label>
</div>
              </div>

              <div className="flex justify-end gap-4 mt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-8 py-3 text-gray-500 border rounded-xl font-bold">Cancel</button>
                <button type="submit" disabled={loading} className="px-10 py-3 text-white bg-[#f34a73] rounded-xl font-bold shadow-lg shadow-pink-200 disabled:opacity-50">
                  {loading ? "Processing..." : isEditing ? "Update Package" : "Create Package"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllProductsPackage;